import { useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

import { useTenant } from "@/providers/session";

const InvoiceBrowserContext = createContext<{
  invoices: string[];
  setInvoiceBrowsingQueue: (
    invoices: string[],
    startingInvoice?: string,
  ) => void;
  next: () => void;
  remove: (invoiceIds?: string[]) => void;
  back: () => void;
  getElementOnQueue: (index: number) => null | string;
  currentIndex: number;
  canGoBack: boolean;
  canGoNext: boolean;
}>({
  invoices: [],
  setInvoiceBrowsingQueue: () => {},
  next: () => {},
  remove: () => {},
  back: () => {},
  getElementOnQueue: () => null,
  canGoBack: false,
  canGoNext: false,
  currentIndex: 0,
});

type Props = PropsWithChildren;

export const InvoiceBrowserProvider = ({ children }: Props) => {
  const invoices = useRef<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const router = useRouter();

  const { companySlug } = useTenant();

  const originRouteRef = useRef<string | null>(null);

  const goBack = useCallback(() => {
    const origin = originRouteRef.current;
    if (origin) {
      originRouteRef.current = null;
      invoices.current = [];
      setCurrentIndex(0);
      navigate({ to: origin, replace: true });
      return;
    }

    if (window.history.length > 1) {
      router.history.back();
      return;
    }

    const currentView = location.pathname.includes("/unternehmen/")
      ? "unternehmen"
      : "buchhaltung";

    navigate({
      to: `/${companySlug}/${currentView}`,
      replace: true,
    });
  }, [companySlug, navigate, location.pathname, router.history]);

  const handleNext = () => {
    const _invoices = invoices.current;

    if (_invoices.length === 0 || currentIndex === _invoices.length - 1) {
      goBack();
    } else {
      setCurrentIndex((prev) => prev + 1);

      const nextInvoice = _invoices[currentIndex + 1];

      const currentView = location.pathname.includes("/unternehmen/")
        ? "unternehmen"
        : "buchhaltung";

      navigate({
        to: `/${companySlug}/${currentView}/rechnung/${nextInvoice}`,
        replace: true,
      });
    }
  };

  const handleBack = () => {
    const _invoices = invoices.current;

    if (currentIndex === 0) {
      goBack();
    } else {
      setCurrentIndex((prev) => prev - 1);

      const previousInvoice = _invoices[currentIndex - 1];

      const currentView = location.pathname.includes("/unternehmen/")
        ? "unternehmen"
        : "buchhaltung";

      navigate({
        to: `/${companySlug}/${currentView}/rechnung/${previousInvoice}`,
        replace: true,
      });
    }
  };

  const handleRemove = (invoiceIds?: string[]) => {
    const currentInvoice = invoices.current[currentIndex];
    let targetInvoice: string;

    if (invoiceIds && invoiceIds.length > 0) {
      // Multiple removal mode
      const wasCurrentInvoiceRemoved = invoiceIds.includes(currentInvoice);

      invoices.current = invoices.current.filter(
        (invoice) => !invoiceIds.includes(invoice),
      );

      const _invoices = invoices.current;

      if (_invoices.length === 0) {
        goBack();
        return;
      }

      let newIndex: number;

      if (wasCurrentInvoiceRemoved) {
        if (currentIndex >= _invoices.length) {
          newIndex = _invoices.length - 1;
        } else {
          newIndex = currentIndex;
        }
        setCurrentIndex(newIndex);
        targetInvoice = _invoices[newIndex];
      } else {
        newIndex = _invoices.findIndex((invoice) => invoice === currentInvoice);
        setCurrentIndex(newIndex);
        targetInvoice = currentInvoice;
      }
    } else {
      invoices.current = invoices.current.filter(
        (_, index) => index !== currentIndex,
      );

      const _invoices = invoices.current;

      if (_invoices.length === 0) {
        goBack();
        return;
      }

      if (currentIndex >= _invoices.length) {
        setCurrentIndex(_invoices.length - 1);
        targetInvoice = _invoices[_invoices.length - 1];
      } else {
        targetInvoice = _invoices[currentIndex];
      }
    }

    const currentView = location.pathname.includes("/unternehmen/")
      ? "unternehmen"
      : "buchhaltung";

    navigate({
      to: `/${companySlug}/${currentView}/rechnung/${targetInvoice}`,
      replace: true,
    });
  };

  const setQueue = (
    _invoices: string[],
    startingInvoice?: string | undefined,
  ) => {
    if (_invoices.length > 0) {
      originRouteRef.current = location.pathname + location.search;
    } else if (_invoices.length === 0) {
      originRouteRef.current = null;
    }

    const index = _invoices.findIndex((invoice) => invoice === startingInvoice);

    invoices.current = _invoices;

    setCurrentIndex(index);
  };

  const getElementOnQueue = (index: number) => {
    return invoices.current[index];
  };

  return (
    <InvoiceBrowserContext.Provider
      value={{
        invoices: invoices.current,
        setInvoiceBrowsingQueue: setQueue,
        next: handleNext,
        remove: handleRemove,
        back: handleBack,
        getElementOnQueue,
        currentIndex,
        canGoBack: currentIndex > 0,
        canGoNext: currentIndex < invoices.current.length - 1,
      }}
    >
      {children}
    </InvoiceBrowserContext.Provider>
  );
};

export const useInvoiceBrowser = () => {
  const context = useContext(InvoiceBrowserContext);
  if (!context)
    throw new Error("useStateDispatch must be used within a StateProvider");
  return context;
};
